import { Search as SearchIcon } from "@mui/icons-material";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
  OutlinedInput,
  TextField,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Patient } from "types";

import { usePatients } from "../../../hooks/usePatients";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";

interface PatientSearchDropDownProps {
  onChange: (value: Patient | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function PatientSearchDropDown({
  onChange,
  placeholder = "Search for patients...",
  label = "Select Patient",
  disabled = false,
  error = false,
  helperText,
}: PatientSearchDropDownProps) {
  const { colors } = useCelleraTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    patients,
    loading,
    error: apiError,
  } = usePatients({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });

  const handleChange = useCallback(
    (event: SelectChangeEvent<Patient | null>) => {
      const selectedValue = event.target.value;

      const patient = patients.find(
        (option) => option.patientId === selectedValue
      );
      if (patient?.patientId === selectedPatient?.patientId) {
        setSelectedPatient(null);
        onChange(null);
        return;
      }

      setSelectedPatient(patient || null);
      onChange(patient || null);

      if (patient) {
        setSearchTerm("");
      }
    },
    [patients, selectedPatient, onChange]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (!selectedPatient) {
      setSearchTerm("");
    }
  }, [selectedPatient]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      if (selectedPatient) {
        setSelectedPatient(null);
        onChange(null);
      }
    },
    [selectedPatient, onChange]
  );

  const contentDetails = useMemo(() => {
    if (loading) {
      return (
        <MenuItem disabled>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
          </Box>
        </MenuItem>
      );
    }
    if (apiError) {
      return (
        <MenuItem disabled>
          <Typography color="error">Error loading patients</Typography>
        </MenuItem>
      );
    }
    if (patients.length === 0) {
      return (
        <MenuItem disabled>
          <Typography>No patients found</Typography>
        </MenuItem>
      );
    }
    return patients.map((patient) => (
      <MenuItem key={patient.patientId} value={patient.patientId}>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {patient.firstName} {patient.lastName}
          </Typography>
        </Box>
      </MenuItem>
    ));
  }, [apiError, loading, patients]);

  return (
    <FormControl error={error || !!apiError}>
      <InputLabel id="patient-select-label">{label}</InputLabel>
      <Select
        labelId="patient-select-label"
        value={selectedPatient}
        onChange={handleChange}
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          return selected ? `${selected.firstName} ${selected.lastName}` : "";
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "2px",
            "& fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.border.light,
            },
            "&:hover fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.border.main,
            },
            "&.Mui-focused fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.secondary.main,
            },
          },
          width: "100%",
        }}
      >
        <MenuItem>
          <TextField
            ref={searchInputRef}
            fullWidth
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="text-gray-400" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "2px",
                "& fieldset": {
                  borderColor: colors.border.light,
                },
                "&:hover fieldset": {
                  borderColor: colors.border.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.secondary.main,
                },
              },
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </MenuItem>
        {contentDetails}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color={error || apiError ? "error" : "text.secondary"}
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText || (apiError ? "Failed to load patients" : undefined)}
        </Typography>
      )}
    </FormControl>
  );
}
